import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface ShopProduct {
  id: string;
  creatorProfileId: string;
  title: string;
  description?: string;
  price: number;
  currency: string;
  productType: 'DIGITAL' | 'PHYSICAL' | 'SERVICE';
  fileUrl?: string;
  imageUrl?: string;
  inventory?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  creatorProfile?: {
    id: string;
    displayName: string;
    user: {
      id: string;
      username: string;
      avatar?: string;
    };
  };
  _count?: {
    purchases: number;
  };
}

export interface CreateProductPayload {
  title: string;
  description?: string;
  price: number;
  currency?: string;
  productType: 'DIGITAL' | 'PHYSICAL' | 'SERVICE';
  inventory?: number;
  isActive?: boolean;
  imageUrl?: string;
  fileUrl?: string;
}

export const shopService = {
  async getProducts(creatorId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/shop/creator/${creatorId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return res.data as { products: ShopProduct[] };
  },

  async getProduct(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/shop/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return res.data as { product: ShopProduct; hasPurchased: boolean };
  },

  async getMyProducts() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/shop/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { products: ShopProduct[] };
  },

  async createProduct(payload: CreateProductPayload, image?: File) {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => formData.append(`${key}[]`, String(v)));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (image) {
      formData.append('image', image);
    }

    const res = await axios.post(`${API_BASE_URL}/api/shop`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data as { product: ShopProduct };
  },

  async updateProduct(id: string, payload: Partial<CreateProductPayload>, image?: File) {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => formData.append(`${key}[]`, String(v)));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (image) {
      formData.append('image', image);
    }

    const res = await axios.put(`${API_BASE_URL}/api/shop/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data as { product: ShopProduct };
  },

  async deleteProduct(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.delete(`${API_BASE_URL}/api/shop/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string };
  },

  async purchaseProduct(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/shop/${id}/purchase`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      message: string;
      purchase: any;
      downloadUrl?: string;
    };
  },

  async getMyPurchases() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/shop/purchases/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      purchases: Array<{
        id: string;
        productId: string;
        patronId: string;
        amount: number;
        currency: string;
        status: string;
        createdAt: string;
        product: ShopProduct;
      }>;
    };
  },
};

